import { create } from 'zustand'
import { supabase } from '@/lib/supabase/client'
import { type CardWithDetails, type ObfuscatedRollCard } from '@/types/card'

interface RollState {
  loading: boolean
  selectedCards: ObfuscatedRollCard[]  // Mudado para usar tokens ofuscados
  canRoll: number
  canPick: boolean
  isAdmin: boolean
  rollTimestamp: number | null  // Timestamp do último roll (para anti-bot timing)
  setLoading: (loading: boolean) => void
  setSelectedCards: (cards: ObfuscatedRollCard[]) => void
  rollCards: (userId: string) => Promise<void>
  reset: () => void
  discardCards: (userId: string) => Promise<void>
  pickCard: (cardToken: string, userId: string) => Promise<void>  // Mudado para usar token
  rebirthCard: (cardId: string, userId: string) => Promise<void>
  checkRollStatus: (userId: string) => Promise<void>
}

export const useRollStore = create<RollState>((set, get) => ({
  loading: false,
  selectedCards: [],
  canRoll: 0,
  canPick: false,
  isAdmin: false,
  rollTimestamp: null,
  setLoading: (loading) => set({ loading }),
  setSelectedCards: (cards) => set({ selectedCards: cards }),
  reset: () => set({ loading: false, selectedCards: [], canRoll: 0, rollTimestamp: null }),
  discardCards: async (userId: string) => {
    try {
      set({ loading: true })

      // Verificar autenticação
      const { data: { session }, error: authError } = await supabase.auth.getSession()
      console.log('Sessão ao descartar:', {
        hasSession: !!session,
        userId: session?.user?.id,
        token: session?.access_token?.slice(0, 20) + '...',
        error: authError
      })

      if (!session?.access_token) {
        throw new Error('Usuário não está autenticado')
      }

      // Resetar o estado local
      get().reset()
    } catch (error) {
      console.error('Erro ao descartar cartas:', error)
      throw error
    } finally {
      set({ loading: false })
    }
  },
  checkRollStatus: async (userId: string) => {
    try {
      // Verificar se é admin
      const { data: isAdmin, error: adminError } = await supabase
        .rpc('is_admin', {
          p_user_id: userId
        })

      if (adminError) throw adminError

      // Verificar status do roll
      const { data: status, error } = await supabase
        .rpc('check_roll_status', {
          p_user_id: userId
        })

      if (error) throw error

      if (status?.[0]) {
        set({ 
          canRoll: status[0].can_roll ?? 0, 
          canPick: status[0].can_pick ?? false,
          isAdmin: !!isAdmin
        })
      }
    } catch (error) {
      console.error('Erro ao verificar status do roll:', error)
      set({ canRoll: 0, canPick: false, isAdmin: false })
    }
  },
  pickCard: async (cardToken: string, userId: string) => {
    try {
      set({ loading: true })

      // 🔒 VALIDAÇÃO ANTI-BOT: Verificar tempo mínimo desde o roll
      const rollTimestamp = get().rollTimestamp
      if (rollTimestamp) {
        const timeSinceRoll = Date.now() - rollTimestamp
        const MIN_TIME_MS = 800 // Mínimo 0.8 segundos (humano mais rápido possível)
        
        if (timeSinceRoll < MIN_TIME_MS) {
          throw new Error('Por favor, visualize as cartas antes de escolher')
        }
      }

      // Buscar o username do usuário
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', userId)
        .single()

      if (profileError) throw profileError
      if (!profileData?.username) throw new Error('Username não encontrado')

      // 🔐 Chamar a nova função com token ofuscado
      // TEMPORÁRIO: Usar função antiga até instalar backend
      const { error } = await supabase.rpc('safe_pick_card', {
        p_card_id: cardToken, // TEMPORÁRIO: cardToken é na verdade o card_id
        p_user_id: userId,
        p_username: profileData.username
      })

      if (error) {
        if (error.message === 'Operação de pick já em andamento') {
          throw new Error('Aguarde, uma operação de pick está em andamento')
        }
        if (error.message.includes('Token inválido')) {
          throw new Error('Sessão expirada. Faça um novo roll.')
        }
        throw error
      }

      // Resetar o estado após selecionar a carta
      set({ 
        loading: false, 
        selectedCards: [],
        canPick: false,
        rollTimestamp: null
      })
    } catch (error) {
      console.error('Erro ao selecionar carta:', error)
      throw error
    } finally {
      set({ loading: false })
    }
  },
  rollCards: async (userId: string) => {
    try {
      set({ loading: true })

      // Se não for admin, verificar status
      if (!get().isAdmin) {
        const { data: status } = await supabase
          .rpc('check_roll_status', {
            p_user_id: userId
          })

        if (!status?.[0]?.can_roll) {
          throw new Error('Você não pode fazer roll agora')
        }
      }

      // 🔐 Chamar a nova função com ofuscação de dados
      // TEMPORÁRIO: Usar função antiga até instalar backend
      const { data: selectedCards, error: rollError } = await supabase
        .rpc('roll_cards', { 
          p_user_id: userId 
        })

      if (rollError) throw rollError
      if (!selectedCards?.length) {
        throw new Error('Não há cartas disponíveis para sorteio')
      }

      // 📝 TEMPORÁRIO: Adaptar formato antigo para o novo (simular tokens)
      // Quando instalar o backend, remover este bloco e descomentar o código abaixo
      const obfuscatedCards: ObfuscatedRollCard[] = selectedCards.map((card: {
        card_id: string;
        card_name: string;
        image_url: string;
        series_id: string;
        series_name: string;
      }, index: number) => ({
        card_token: card.card_id, // TEMPORÁRIO: Usar ID como token
        image_url: card.image_url,
        series_id: card.series_id,
        series_name: card.series_name,
        roll_session_id: 'temp-session', // TEMPORÁRIO
        card_index: index
      }))

      /* TODO: Descomentar após instalar backend
      const obfuscatedCards: ObfuscatedRollCard[] = selectedCards.map((card: {
        card_token: string;
        image_url: string;
        series_id: string;
        series_name: string;
        roll_session_id: string;
        card_index: number;
      }) => ({
        card_token: card.card_token,
        image_url: card.image_url,
        series_id: card.series_id,
        series_name: card.series_name,
        roll_session_id: card.roll_session_id,
        card_index: card.card_index
      }))
      obfuscatedCards.sort((a, b) => a.card_index - b.card_index)
      */

      set({ 
        selectedCards: obfuscatedCards,
        canRoll: 0,
        canPick: true,
        rollTimestamp: Date.now() // 🕐 Registrar timestamp do roll para anti-bot
      })

      // Atualizar o status após o roll
      await get().checkRollStatus(userId)

    } catch (error) {
      console.error('Erro ao realizar roll:', error)
      throw error
    } finally {
      set({ loading: false })
    }
  },
  rebirthCard: async (cardId: string, userId: string) => {
    try {
      set({ loading: true })

      // Verificar se o usuário possui a carta
      const { data: cardCheck, error: checkError } = await supabase
        .from('user_inventory')
        .select('id, quantity')
        .eq('user_id', userId)
        .eq('server_card_id', cardId)
        .single()

      if (checkError) {
        console.error('Erro ao verificar carta:', checkError)
        throw new Error('Erro ao verificar carta no inventário')
      }

      if (!cardCheck || cardCheck.quantity <= 0) {
        throw new Error('Você não possui mais esta carta')
      }

      // Verificar se a carta está em alguma proposta de troca pendente
      const { data: tradeCheck, error: tradeError } = await supabase
        .from('trade_requests')
        .select('id')
        .eq('status', 'pending')
        .or(`and(from_card_id.eq.${cardCheck.id},from_user_id.eq.${userId}),and(to_card_id.eq.${cardCheck.id},to_user_id.eq.${userId})`)
        .limit(1)

      if (tradeError) {
        console.error('Erro ao verificar propostas de troca:', tradeError)
        throw new Error('Erro ao verificar propostas de troca')
      }

      if (tradeCheck && tradeCheck.length > 0) {
        throw new Error('Esta carta está em uma proposta de troca pendente. Cancele a proposta antes de desencantá-la.')
      }

      // Buscar o username do usuário
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', userId)
        .single()

      if (profileError) {
        console.error('Erro ao buscar perfil:', profileError)
        throw new Error(`Erro ao buscar perfil: ${profileError.message}`)
      }
      if (!profileData?.username) throw new Error('Username não encontrado')

      console.log('Tentando fazer rebirth da carta via RPC')
      // Chamar a função RPC para rebirth da carta
      const { data, error } = await supabase.rpc('rebirth_card', {
        p_card_id: cardId,
        p_user_id: userId,
        p_username: profileData.username
      })

      console.log('Resposta do RPC rebirth_card:', { data, error })

      if (error) {
        console.error('Erro detalhado do RPC:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        })
        throw new Error(`Erro ao fazer rebirth: ${error.message}`)
      }

    } catch (error) {
      console.error('Erro ao fazer rebirth da carta:', error)
      if (error instanceof Error) {
        throw error // Propagar o erro original sem adicionar prefixo extra
      } else {
        throw new Error('Erro desconhecido ao fazer rebirth da carta')
      }
    } finally {
      set({ loading: false })
    }
  }
})) 